import { createSign, createPrivateKey } from "node:crypto";

/**
 * Sign a GitHub App JWT (RS256, 10-min TTL, iss=appId).
 *
 * Used to authenticate to GitHub's App-level endpoints — most importantly
 * POST /app/installations/{id}/access_tokens to mint an installation token.
 *
 * Zero npm deps — uses Node's built-in crypto. iat carries a 30-second clock-
 * skew safety margin (GitHub recommends iat slightly in the past).
 *
 * @throws if appId is not a numeric string or privateKeyPem is not a valid PEM.
 */
export function signAppJwt(args: { appId: string; privateKeyPem: string }): string {
  const appIdNum = Number(args.appId);
  if (!Number.isInteger(appIdNum) || appIdNum <= 0) {
    throw new Error(`signAppJwt: appId must be a positive numeric string, got '${args.appId}'`);
  }

  const now = Math.floor(Date.now() / 1000);
  const header = { alg: "RS256", typ: "JWT" };
  const payload = { iat: now - 30, exp: now - 30 + 600, iss: appIdNum };

  const b64url = (obj: unknown) =>
    Buffer.from(JSON.stringify(obj)).toString("base64url");
  const unsigned = `${b64url(header)}.${b64url(payload)}`;

  const sign = createSign("RSA-SHA256");
  sign.update(unsigned);
  sign.end();
  const signature = sign.sign(createPrivateKey(args.privateKeyPem)).toString("base64url");
  return `${unsigned}.${signature}`;
}
